import { motion } from "framer-motion";
import { fadeIn } from "../components/common/motionFrameVarients";
import Footer from "../components/common/Footer";
import HighlightText from "../components/core/HomePage/HighlightText";
import CTAButton from "../components/core/HomePage/Button";

// Sample job openings data
const jobs = [
  {
    id: 1,
    title: "Full Stack Developer",
    type: "Full Time",
    location: "Remote",
    description: "Build cutting-edge educational platforms with modern tech stack",
    skills: ["React", "Node.js", "MongoDB", "AWS"]
  },
  {
    id: 2,
    title: "DevOps Engineer",
    type: "Contract",
    location: "Hybrid",
    description: "Manage cloud infrastructure and CI/CD pipelines",
    skills: ["Docker", "Kubernetes", "Jenkins", "Terraform"]
  },
  {
    id: 3,
    title: "Technical Content Creator",
    type: "Full Time",
    location: "Remote",
    description: "Develop engaging coding tutorials and course materials",
    skills: ["Technical Writing", "Video Production", "Web Development", "Teaching"]
  },
];

// Company benefits
const benefits = [
  { icon: "💰", title: "Competitive Salary", desc: "Industry-standard compensation with ESOPs" },
  { icon: "⏰", title: "Flexible Hours", desc: "Results-oriented work environment" },
  { icon: "📚", title: "Learning Budget", desc: "Annual budget for skill development" },
  { icon: "💻", title: "Tech Setup", desc: "Top-notch equipment for your work" },
];

// Company culture values
const cultureValues = [
  { icon: "🚀", title: "Innovation First", description: "Continuous improvement in everything we build" },
  { icon: "👩‍💻", title: "Learner Focused", description: "Every decision is made through learner's perspective" },
  { icon: "🌍", title: "Global Impact", description: "Creating solutions that scale worldwide" },
  { icon: "🤝", title: "Collaboration", description: "Working together to achieve common goals" },
  { icon: "🔄", title: "Adaptability", description: "Embracing change and new technologies" },
  { icon: "🎯", title: "Results Driven", description: "Focused on achieving meaningful outcomes" },
];

function Careers() {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.1 }}
      className="w-full overflow-hidden bg-richblack-900 text-white"
    >
      {/* Hero Section */}
      <div className="relative w-full h-[80vh]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-richblack-900/90 flex items-center justify-center">
          <div className="text-center px-4">
            <motion.h1
              variants={fadeIn("up", 0.2)}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Shape the Future of <HighlightText text="Tech Education" />
            </motion.h1>
            <motion.p
              variants={fadeIn("up", 0.4)}
              className="text-richblack-200 text-lg md:text-xl max-w-4xl mx-auto mb-8"
            >
              Join our mission to make coding education accessible and effective for everyone around the world
            </motion.p>
            <motion.div variants={fadeIn("up", 0.6)}>
              <CTAButton
                active={true}
                linkto="#open-positions"
              >
                View Open Positions
              </CTAButton>
            </motion.div>
          </div>
        </div>
        <img 
          src="https://images.pexels.com/photos/3184423/pexels-photo-3184423.jpeg?auto=compress&cs=tinysrgb&w=1200" 
          alt="Kode Vedic Team" 
          className="w-full h-full object-cover"
        />
      </div>

      {/* Current Openings */}
      <section id="open-positions" className="w-11/12 max-w-maxContent mx-auto py-20 scroll-mt-24">
        <motion.h2
          variants={fadeIn("up", 0.2)}
          className="text-3xl md:text-4xl font-bold text-center mb-4"
        >
          Current <HighlightText text="Open Positions" />
        </motion.h2>
        
        <motion.p
          variants={fadeIn("up", 0.3)}
          className="text-richblack-300 text-center max-w-3xl mx-auto mb-12"
        >
          Join our talented team of professionals and help us transform the landscape of tech education
        </motion.p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {jobs.map((job, index) => (
            <motion.div
              key={job.id}
              variants={fadeIn("up", 0.2 + index * 0.1)}
              className="bg-richblack-800 rounded-xl overflow-hidden hover:shadow-lg hover:transform hover:scale-105 transition-all duration-300"
            >
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-richblack-700 text-yellow-50 rounded-full text-sm mb-4">
                  {job.type} • {job.location}
                </span>
                <h3 className="text-2xl font-bold mb-3">{job.title}</h3>
                <p className="text-richblack-100 mb-4">{job.description}</p>
                
                <div className="mb-6">
                  <p className="text-sm text-richblack-300 mb-2">Required Skills:</p>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, idx) => (
                      <span key={idx} className="bg-richblack-700 px-2 py-1 rounded-md text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                
                <CTAButton
                  active={true}
                  linkto={`/careers/apply/${job.id}`}
                  className="w-full text-center"
                >
                  Apply Now
                </CTAButton>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Join Us */}
      <section className="bg-richblack-800 py-20">
        <div className="w-11/12 max-w-maxContent mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
                Why <HighlightText text="Join Kode Vedic?" />
              </h2>
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            
            <div>
              
              <p className="text-richblack-300 mb-8">
                At Kode Vedic, we're building more than just coding courses - we're creating
                a movement to democratize tech education. Join our passionate team of 
                educators, developers, and innovators to make a lasting impact.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    variants={fadeIn("up", 0.3 + index * 0.1)}
                    className="bg-richblack-700 p-5 rounded-lg hover:bg-richblack-600 transition-colors"
                  >
                    <div className="text-2xl mb-3">{benefit.icon}</div>
                    <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                    <p className="text-sm text-richblack-300">{benefit.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <motion.div
              variants={fadeIn("left", 0.3)}
              className="rounded-xl overflow-hidden shadow-2xl"
            >
              <img 
                src="https://images.pexels.com/photos/1595385/pexels-photo-1595385.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Team Collaboration" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="w-11/12 max-w-maxContent mx-auto py-20">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Our <HighlightText text="Culture" />
          </h2>
          <p className="text-richblack-300 max-w-3xl mx-auto">
            We foster an environment that encourages innovation, collaboration, and continuous growth
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cultureValues.map((value, index) => (
            <motion.div
              key={index}
              variants={fadeIn("up", 0.2 + index * 0.1)}
              className="bg-richblack-800 p-6 rounded-xl hover:bg-richblack-700 transition-all duration-300"
            >
              <div className="text-3xl mb-4">{value.icon}</div>
              <h3 className="text-xl font-bold mb-3">{value.title}</h3>
              <p className="text-richblack-300">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="bg-richblack-800 py-20">
        <div className="w-11/12 max-w-maxContent mx-auto">
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              What Our <HighlightText text="Team Says" />
            </h2>
          </motion.div>

          <motion.div
            variants={fadeIn("up", 0.3)}
            className="bg-richblack-700 p-8 rounded-xl relative"
          >
            <div className="text-4xl text-yellow-50 mb-4">"</div>
            <p className="text-xl text-richblack-100 mb-6">
              Joining Kode Vedic was the best career decision I've made. The culture here genuinely supports growth, 
              innovation, and work-life balance. Every day I feel like I'm contributing to something meaningful.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-richblack-600"></div>
              <div>
                <h4 className="font-semibold">Sarah Johnson</h4>
                <p className="text-sm text-richblack-300">Senior Developer, 2 years at Kode Vedic</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-b from-richblack-900 to-richblack-800 text-center py-20">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="w-11/12 max-w-maxContent mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Don't See Your <HighlightText text="Perfect Role?" />
          </h2>
          <p className="text-richblack-300 mb-8 max-w-2xl mx-auto">
            We're always looking for passionate individuals to join our team. Send us your resume
            and tell us how you can contribute to our mission of democratizing tech education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton
              active={true}
              linkto="/contact"
            >
              Submit General Application
            </CTAButton>
            <CTAButton
              active={false}
              linkto="/about"
            >
              Learn More About Us
            </CTAButton>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="w-11/12 max-w-maxContent mx-auto py-20">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Frequently Asked <HighlightText text="Questions" />
          </h2>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeIn("up", 0.3)} className="mb-6">
            <h3 className="text-xl font-semibold mb-2">What is the hiring process like?</h3>
            <p className="text-richblack-300">
              Our hiring process typically includes an initial screening, a technical or skills assessment, 
              and 1-2 interviews with team members. We aim to make decisions within 2-3 weeks.
            </p>
          </motion.div>

          <motion.div variants={fadeIn("up", 0.4)} className="mb-6">
            <h3 className="text-xl font-semibold mb-2">Do you offer relocation assistance?</h3>
            <p className="text-richblack-300">
              Yes, we offer relocation assistance for certain roles. This is discussed during the interview process 
              based on the position and your specific circumstances.
            </p>
          </motion.div>

          <motion.div variants={fadeIn("up", 0.5)}>
            <h3 className="text-xl font-semibold mb-2">What's the work environment like?</h3>
            <p className="text-richblack-300">
              We have a flexible, collaborative work environment with a mix of remote and in-office options. 
              Our team works across different time zones with core collaboration hours.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />

      
    </motion.div>
  );
}

export default Careers;